import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAppContext } from '../../../contexts/AppContext';
import { useAuth } from '../../../contexts/AuthContext';
import { generatePartnerCodeFromCompany } from '../../../domain/partners/commission';
import { formatCnpjDisplay } from '../../../domain/partners/application';
import { getGaClientId } from '../../../lib/analytics/gaClientId';
import { setAuthReturnPath } from '../../../lib/auth/authReturnPath';
import { clearPendingOnboarding } from '../../onboarding/pendingOnboarding';
import { formatPhoneWithDDI } from '../../../utils/formatters';
import { notify } from '../../../utils/toast';
import {
  validatePartnerPublicRegistration,
  type PartnerPublicRegistrationField,
} from '../domain/publicRegistration';

function mapSignupError(err: unknown): string {
  const message =
    err instanceof Error ? err.message : typeof err === 'string' ? err : 'Falha ao cadastrar.';
  const lower = message.toLowerCase();
  const code =
    err && typeof err === 'object' && 'code' in err
      ? String((err as { code?: string }).code ?? '')
      : '';

  if (
    lower.includes('failed to fetch') ||
    lower.includes('networkerror') ||
    lower.includes('err_connection')
  ) {
    return 'Erro de conexão. Verifique sua internet e tente novamente.';
  }
  if (lower.includes('already registered') || lower.includes('já está em uso')) {
    return 'Este email já está em uso. Entre na conta e candidate-se.';
  }
  if (code === 'weak_password') {
    return 'Use uma senha mais forte: pelo menos 8 caracteres, misturando maiúsculas, minúsculas, números e símbolos.';
  }
  if (lower.includes('password') || lower.includes('invalid login')) {
    return 'Conta criada, mas não foi possível entrar. Faça login e complete a candidatura.';
  }
  return message || 'Falha ao cadastrar. Tente novamente.';
}

export function usePartnerPublicApply() {
  const { setCurrentPage } = useAppContext();
  const { navigateToLogin } = useAuth();

  const [fullName, setFullNameState] = useState('');
  const [email, setEmailState] = useState('');
  const [phone, setPhoneState] = useState('+55 ');
  const [password, setPasswordState] = useState('');
  const [confirmPassword, setConfirmPasswordState] = useState('');
  const [companyName, setCompanyNameState] = useState('');
  const [cnpj, setCnpjState] = useState('');
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<PartnerPublicRegistrationField, string>>
  >({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const clearFieldError = useCallback((field: PartnerPublicRegistrationField) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const goToLogin = useCallback(() => {
    setAuthReturnPath('/parceiro/aplicar');
    navigateToLogin();
  }, [navigateToLogin]);

  const submit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      setFormError(null);

      const validation = validatePartnerPublicRegistration({
        fullName,
        email,
        phone,
        password,
        confirmPassword,
        companyName,
        cnpj,
      });

      if (validation.ok === false) {
        setFieldErrors({ [validation.field]: validation.message });
        setFormError(validation.message);
        return;
      }

      const { payload } = validation;

      setSubmitting(true);
      setFieldErrors({});

      // Partner signup must not enter CRM onboarding funnel.
      localStorage.removeItem('adlitics_new_registration');
      clearPendingOnboarding();

      try {
        const ga_client_id = getGaClientId();
        const { data, error } = await supabase.functions.invoke('auth-signup', {
          body: {
            email: payload.email,
            password: payload.password,
            full_name: payload.full_name,
            phone: payload.phone,
            invite_token: null,
            plan: 'basic',
            ga_client_id,
          },
        });

        if (data?.error) {
          const signupError = new Error(data.error) as Error & { code?: string };
          if (data.code === 'weak_password') signupError.code = 'weak_password';
          throw signupError;
        }
        if (error) throw error;

        const { error: signInError } = await supabase.auth.signInWithPassword({
          email: payload.email,
          password: payload.password,
        });
        if (signInError) throw signInError;

        const {
          data: { user },
          error: userError,
        } = await supabase.auth.getUser();
        if (userError || !user) {
          throw new Error('Conta criada, mas a sessão não ficou ativa. Faça login e candidate-se.');
        }

        const code = generatePartnerCodeFromCompany(payload.application.company_name);
        const { error: insertError } = await supabase.from('partners').insert({
          user_id: user.id,
          code,
          company_name: payload.application.company_name,
          cnpj: payload.application.cnpj,
          status: payload.application.status,
          commission_rate: payload.application.commission_rate,
        });

        if (insertError) {
          if (insertError.code === '23505') {
            notify.success('Conta criada. Você já possui candidatura de parceiro.');
            setCurrentPage('parceiro');
            return;
          }
          throw insertError;
        }

        notify.success('Conta criada e candidatura enviada! Aguarde a aprovação.');
        setCurrentPage('parceiro');
      } catch (err) {
        localStorage.removeItem('adlitics_new_registration');
        clearPendingOnboarding();
        const message = mapSignupError(err);
        if (mountedRef.current) {
          setFormError(message);
        }
        notify.error(message);
      } finally {
        if (mountedRef.current) setSubmitting(false);
      }
    },
    [
      fullName,
      email,
      phone,
      password,
      confirmPassword,
      companyName,
      cnpj,
      setCurrentPage,
    ],
  );

  return {
    fullName,
    email,
    phone,
    password,
    confirmPassword,
    companyName,
    cnpj,
    fieldErrors,
    formError,
    submitting,
    setFullName: (v: string) => {
      setFullNameState(v);
      clearFieldError('fullName');
    },
    setEmail: (v: string) => {
      setEmailState(v);
      clearFieldError('email');
    },
    setPhone: (v: string) => {
      setPhoneState(formatPhoneWithDDI(v));
      clearFieldError('phone');
    },
    setPassword: (v: string) => {
      setPasswordState(v);
      clearFieldError('password');
    },
    setConfirmPassword: (v: string) => {
      setConfirmPasswordState(v);
      clearFieldError('confirmPassword');
    },
    setCompanyName: (v: string) => {
      setCompanyNameState(v);
      clearFieldError('companyName');
    },
    setCnpj: (v: string) => {
      setCnpjState(formatCnpjDisplay(v));
      clearFieldError('cnpj');
    },
    submit,
    goToLogin,
  };
}
